import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridLogicOperator,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";

import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { projectsPrefix } from "../../../services/ProjectsServices.ts";
import { getDiffDaysMessage } from "../../../utils/main.ts";
import AccordionTable from "../../datagrid/AccordionTable.tsx";

export default function MedicationsDataExpandTable() {
  const { id: projectId } = useContext(ProjectContext)!;
  const { id, medicationsCount } = useContext(PatientContext)!;

  // this uses the 'create' method because it's a post,
  // it's not actually creating anything
  const {
    error: medsError,
    create: fetchMeds,
    isLoading: medsLoading,
  } = useFetch<PagedResponse<Medication>, FilterPageSortRequest>();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filter, setFilter] = useState<GridFilterModel>();
  const [rows, setRows] = useState<Medication[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [shouldFetchMeds, setShouldFetchMeds] = useState(false);
  const [viewStateReady, setViewStateReady] = useState(false);

  // Only keep 'contains' default operators
  const stringFilterOperators = getGridStringOperators().filter(
    (operator) => operator.value === "contains",
  );

  // Only keep "is" operator.  We present as a list of 2 options, but it's actually a boolean underneath.
  // Therefore "not", and "isAnyOf" operators don't add any new functionality
  const administeredFilterOperators = getGridSingleSelectOperators().filter(
    (operator) => operator.value === "is",
  );

  const medsTableFields: GridColDef<Medication>[] = [
    {
      field: "isAdministered",
      headerName: "Record Type",
      width: 120,
      description:
        "A physician's Order (Plan) or a confirmed Administration (Action).",
      type: "singleSelect",
      valueOptions: [
        { value: true, label: "Administration" },
        { value: false, label: "Order" },
      ],
      filterOperators: administeredFilterOperators,
    },
    {
      field: "therapeuticClass",
      headerName: "Therapeutic Class",
      width: 175,
      filterOperators: stringFilterOperators,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterOperators: stringFilterOperators,
    },
    {
      field: "eventOffset",
      headerName: "Event Date/Time",
      width: 125,
      filterable: false,
      description:
        "The timestamp for the primary event—when the Order was placed or when the Administration occurred.",
      valueGetter: (eventOffset) => {
        return getDiffDaysMessage(eventOffset);
      },
    },
    {
      field: "startOffset",
      headerName: "Start Date/Time",
      width: 125,
      filterable: false,
      description:
        "The scheduled (for Orders) or actual time (for Administrations) that the medication treatment was initiated.",
      valueGetter: (startOffset) => {
        return getDiffDaysMessage(startOffset);
      },
    },
    {
      field: "stopOffset",
      headerName: "End Date/Time",
      width: 125,
      filterable: false,
      description:
        "The time the medication treatment was scheduled to stop (for Orders) or discontinued (for Administrations).",
      valueGetter: (stopOffset) => {
        return getDiffDaysMessage(stopOffset);
      },
    },
    { field: "status", headerName: "Status", width: 125, filterable: false },
    {
      field: "frequency",
      headerName: "Frequency",
      width: 125,
      filterable: false,
    },
    {
      field: "route",
      headerName: "Route",
      width: 125,
      filterOperators: stringFilterOperators,
    },
  ];

  useEffect(() => {
    if (shouldFetchMeds && viewStateReady && !medsLoading && !medsError) {
      void fetchMeds({
        path: `${projectsPrefix}/${projectId}/patients/${id}/medications`,
        body: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: [...sortModel],
          search: filter?.quickFilterValues?.join(" ") || "",
          filters: filter?.items,
          filterLogicalOperator: filter?.logicOperator?.toUpperCase(),
        },
        postResponseAction: (data) => {
          setRows(data?.results ?? []);
          setRowCount(data?.rowCount ?? 0);
          setShouldFetchMeds(false);
        },
      });
    }
  }, [
    fetchMeds,
    id,
    projectId,
    medsLoading,
    medsError,
    shouldFetchMeds,
    viewStateReady,
    paginationModel,
    filter,
    sortModel,
  ]);

  return (
    <AccordionTable
      title={"Medications"}
      highlightField={"createdAt"}
      highlightCount={medicationsCount?.highlight}
      totalCount={medicationsCount?.total ?? 0}
      tableHeight={400}
      triggerFetch={() => setShouldFetchMeds(true)}
      pageSizeOptions={[10, 50, 100]}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={(model) => {
        setPaginationModel(model);
        setShouldFetchMeds(true);
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={(sortModel) => {
        setSortModel(sortModel);
        setShouldFetchMeds(true);
      }}
      filterMode="server"
      onFilterModelChange={(filterModel) => {
        setFilter(filterModel);
        setShouldFetchMeds(true);
      }}
      rowCount={rowCount || 0}
      loading={shouldFetchMeds}
      slotProps={{
        filterPanel: {
          logicOperators: [GridLogicOperator.And],
        },
        toolbar: {
          showQuickFilter: true,
          quickFilterColumns: "name or class",
          onViewStateReady: () => setViewStateReady(true),
        },
      }}
      columns={medsTableFields}
      rows={rows || []}
      disableRowGrouping
      tableId="medications-data-table"
    />
  );
}
